import {
  CapsuleGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry,
  TorusGeometry,
  type BufferGeometry,
} from "three";

import type { ResidentPresentation } from "../../sim/neighborhood/index.ts";
import {
  resolvePresentedHairStyle,
  type CharacterAppearance,
} from "../../sim/appearance.ts";
import { NEIGHBORHOOD_CONFIG } from "../../config/neighborhood.ts";
import { CHARACTER_PRESENTATION_CONFIG } from "../../config/character.ts";
import { APPEARANCE_PRESENTATION_CONFIG } from "../../config/appearance.ts";
import {
  applyGarmentLayerSet,
  createGarmentGeometrySet,
  createGarmentLayerSet,
  disposeGarmentGeometrySet,
  setGarmentTrimColour,
  type GarmentLayerSet,
} from "../characterView/garments.ts";
import {
  createCharacterFeatureGeometrySet,
  disposeCharacterFeatureGeometrySet,
} from "../characterView/features.ts";
import {
  createLowerBodyGeometrySet,
  disposeLowerBodyGeometrySet,
  footwearBottomHeight,
} from "../characterView/lowerBody.ts";

interface ActorMaterials {
  readonly outerwear: MeshStandardMaterial;
  readonly lower: MeshStandardMaterial;
  readonly accent: MeshStandardMaterial;
  readonly skin: MeshStandardMaterial;
  readonly hair: MeshStandardMaterial;
  readonly eyes: MeshStandardMaterial;
  readonly accessory: MeshStandardMaterial;
  readonly boot: MeshStandardMaterial;
  readonly trim: MeshStandardMaterial;
}

interface ActorParts {
  readonly presentationRoot: Group;
  readonly upperBody: Group;
  readonly hips: Mesh;
  readonly garments: GarmentLayerSet;
  readonly belly: Mesh;
  readonly leftArm: Group;
  readonly rightArm: Group;
  readonly leftSleeve: Mesh;
  readonly rightSleeve: Mesh;
  readonly leftHand: Mesh;
  readonly rightHand: Mesh;
  readonly leftLeg: Group;
  readonly rightLeg: Group;
  readonly leftTrouser: Mesh;
  readonly rightTrouser: Mesh;
  readonly leftFootwear: Mesh;
  readonly rightFootwear: Mesh;
  readonly skirt: Mesh;
  readonly head: Mesh;
  readonly nose: Mesh;
  readonly mouth: Mesh;
  readonly leftBrow: Mesh;
  readonly rightBrow: Mesh;
  readonly leftEye: Mesh;
  readonly rightEye: Mesh;
  readonly hair: Mesh;
  readonly leftLens: Mesh;
  readonly rightLens: Mesh;
  readonly headwear: Mesh;
  readonly neckwear: Mesh;
  readonly carried: Mesh;
}

interface Actor {
  readonly object: Group;
  readonly materials: ActorMaterials;
  readonly parts: ActorParts;
  readonly slotIndex: number;
  residentId: string | null;
  appearance: Readonly<CharacterAppearance> | null;
}

interface SharedGeometry {
  readonly hips: BufferGeometry;
  readonly head: BufferGeometry;
  readonly belly: BufferGeometry;
  readonly hand: BufferGeometry;
  readonly brow: BufferGeometry;
  readonly mouth: BufferGeometry;
  readonly skirt: BufferGeometry;
  readonly nose: BufferGeometry;
  readonly eye: BufferGeometry;
}

function createMaterial(
  name: string,
  colour: number,
  roughness = 0.94,
): MeshStandardMaterial {
  return new MeshStandardMaterial({
    name,
    color: colour,
    roughness,
    metalness: 0,
  });
}

function setShadow(mesh: Mesh): Mesh {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

const NPC_HEAD_RADIUS_M =
  0.19 * CHARACTER_PRESENTATION_CONFIG.proportions.headScale;
const NPC_LEG_PIVOT_HEIGHT_M = 0.72;
const NPC_FOOTWEAR_LOCAL_Y_M = -0.65;
const NPC_FOOTWEAR_LOCAL_Z_M = 0.06;

function browGeometry(): BufferGeometry {
  const geometry = new CapsuleGeometry(0.008, 0.062, 1, 4);
  geometry.rotateZ(Math.PI * 0.5);
  return geometry;
}

function npcGroundOffsetM(
  actor: Readonly<Actor>,
  appearance: Readonly<CharacterAppearance>,
  leftLegX: number,
  rightLegX: number,
): number {
  const stature = APPEARANCE_PRESENTATION_CONFIG
    .statureScaleById[appearance.stature];
  const limbScale = APPEARANCE_PRESENTATION_CONFIG
    .bodyScaleByShape[appearance.bodyShape].limb;
  const lowestBootM = Math.min(
    footwearBottomHeight(
      actor.parts.leftFootwear.geometry,
      NPC_LEG_PIVOT_HEIGHT_M,
      NPC_FOOTWEAR_LOCAL_Y_M,
      NPC_FOOTWEAR_LOCAL_Z_M,
      leftLegX,
      stature,
      limbScale,
    ),
    footwearBottomHeight(
      actor.parts.rightFootwear.geometry,
      NPC_LEG_PIVOT_HEIGHT_M,
      NPC_FOOTWEAR_LOCAL_Y_M,
      NPC_FOOTWEAR_LOCAL_Z_M,
      rightLegX,
      stature,
      limbScale,
    ),
  );
  return CHARACTER_PRESENTATION_CONFIG.soleClearanceMeters +
    Math.max(0, -lowestBootM);
}

function stableMotionPhase(residentId: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < residentId.length; index += 1) {
    hash ^= residentId.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return ((hash >>> 0) / 0xffff_ffff) * Math.PI * 2;
}

function stableMotionVariation(residentId: string): number {
  return (stableMotionPhase(residentId + ":cadence") / (Math.PI * 2)) *
    0.18 - 0.09;
}

function setHeadVerticalOffset(parts: ActorParts, offsetMeters: number): void {
  parts.head.position.y = 1.72 + offsetMeters;
  parts.nose.position.y = 1.715 + offsetMeters;
  parts.mouth.position.y = 1.635 + offsetMeters;
  parts.leftEye.position.y = 1.765 + offsetMeters;
  parts.rightEye.position.y = 1.765 + offsetMeters;
  parts.leftBrow.position.y = 1.825 + offsetMeters;
  parts.rightBrow.position.y = 1.825 + offsetMeters;
  parts.hair.position.y = 1.72 + offsetMeters;
  parts.leftLens.position.y = 1.765 + offsetMeters;
  parts.rightLens.position.y = 1.765 + offsetMeters;
  parts.headwear.position.y = 1.72 + offsetMeters;
  parts.neckwear.position.y = 1.52 + offsetMeters;
}

export class NpcPopulationView {
  readonly object = new Group();
  readonly #actors: readonly Actor[];
  readonly #actorByResidentId = new Map<string, Actor>();
  readonly #garmentGeometries = createGarmentGeometrySet();
  readonly #featureGeometries = createCharacterFeatureGeometrySet(
    NPC_HEAD_RADIUS_M,
  );
  readonly #lowerBodyGeometries = createLowerBodyGeometrySet(0.7);
  readonly #shared: SharedGeometry = {
    hips: new CylinderGeometry(0.19, 0.23, 0.22, 7),
    head: new SphereGeometry(NPC_HEAD_RADIUS_M, 12, 9),
    belly: new SphereGeometry(0.24, 8, 5),
    hand: new SphereGeometry(0.068, 7, 5),
    brow: browGeometry(),
    mouth: new TorusGeometry(0.04, 0.006, 4, 9, Math.PI),
    skirt: new CylinderGeometry(0.235, 0.31, 0.62, 9),
    nose: new ConeGeometry(0.042, 0.135, 6),
    eye: new SphereGeometry(0.027, 7, 5),
  };

  constructor(
    capacity: number = NEIGHBORHOOD_CONFIG.populationRenderCapacity,
  ) {
    this.object.name = "living-neighbourhood-population";
    this.#actors = Array.from({ length: capacity }, (_, index) =>
      this.#createActor(index)
    );
  }

  setPresentations(
    presentations: readonly Readonly<ResidentPresentation>[],
    animationTimeSeconds = 0,
  ): void {
    const bounded = presentations.slice(0, this.#actors.length);
    const presentedIds = new Set(bounded.map((resident) => resident.id));
    for (const actor of this.#actors) {
      if (
        actor.residentId !== null &&
        !presentedIds.has(actor.residentId)
      ) {
        this.#actorByResidentId.delete(actor.residentId);
        actor.residentId = null;
        actor.appearance = null;
        actor.object.name = "resident-slot:" + actor.slotIndex;
        actor.object.visible = false;
        actor.object.userData = {};
      }
    }
    for (const resident of bounded) {
      let actor = this.#actorByResidentId.get(resident.id);
      if (actor === undefined) {
        actor = this.#actors.find((candidate) =>
          candidate.residentId === null
        );
        if (actor === undefined) {
          break;
        }
        actor.residentId = resident.id;
        this.#actorByResidentId.set(resident.id, actor);
      }
      actor.object.visible = true;
      actor.object.name = "resident:" + resident.id;
      actor.object.position.set(
        resident.position.x,
        resident.position.y,
        resident.position.z,
      );
      actor.object.rotation.y = resident.yawRadians;
      if (actor.appearance !== resident.appearance) {
        this.#applyAppearance(actor, resident.appearance);
        actor.appearance = resident.appearance;
        actor.object.userData.appearance = structuredClone(resident.appearance);
      }
      this.#applyMotion(actor, resident, animationTimeSeconds);
      Object.assign(actor.object.userData, {
        residentId: resident.id,
        name: resident.name,
        role: resident.role,
        named: resident.named,
        activity: resident.activity,
        locomotionSpeedMps: resident.locomotionSpeedMps,
        expression: resident.expression ?? "neutral",
      });
    }
  }

  dispose(): void {
    for (const actor of this.#actors) {
      for (const material of Object.values(actor.materials)) {
        material.dispose();
      }
    }
    for (const geometry of Object.values(this.#shared)) {
      geometry.dispose();
    }
    disposeGarmentGeometrySet(this.#garmentGeometries);
    disposeCharacterFeatureGeometrySet(this.#featureGeometries);
    disposeLowerBodyGeometrySet(this.#lowerBodyGeometries);
    this.#actorByResidentId.clear();
    this.object.removeFromParent();
    this.object.clear();
  }

  #applyAppearance(
    actor: Actor,
    appearance: Readonly<CharacterAppearance>,
  ): void {
    const body =
      APPEARANCE_PRESENTATION_CONFIG.bodyScaleByShape[appearance.bodyShape];
    const face =
      APPEARANCE_PRESENTATION_CONFIG.faceScaleByShape[appearance.faceShape];
    const outerwear = APPEARANCE_PRESENTATION_CONFIG
      .outerwearSilhouetteById[appearance.outerwear];
    const stature =
      APPEARANCE_PRESENTATION_CONFIG.statureScaleById[appearance.stature];
    const age =
      APPEARANCE_PRESENTATION_CONFIG.agePresentationById[appearance.ageGroup];
    const { parts, materials } = actor;

    parts.presentationRoot.scale.set(1, stature, 1);
    parts.hips.scale.set(body.hip, 1, body.hip);
    parts.belly.visible = body.belly > 0.25;
    parts.belly.position.z = 0.2 + body.belly * 0.025;
    parts.belly.scale.set(
      body.torso * (0.58 + body.belly * 0.08),
      0.7 + body.belly * 0.1,
      body.depth * (0.26 + body.belly * 0.15),
    );
    const shoulderJointOffset = CHARACTER_PRESENTATION_CONFIG.proportions
      .shoulderJointOffsetMeters * body.shoulder;
    parts.leftArm.position.x = shoulderJointOffset;
    parts.rightArm.position.x = -shoulderJointOffset;
    const sleeveWidth = body.limb * outerwear.sleeveWidth;
    parts.leftArm.scale.set(sleeveWidth, 1, sleeveWidth);
    parts.rightArm.scale.set(sleeveWidth, 1, sleeveWidth);
    const garmentGeometry = this.#garmentGeometries[appearance.outerwear];
    parts.leftSleeve.geometry = garmentGeometry.sleeve;
    parts.rightSleeve.geometry = garmentGeometry.sleeve;
    parts.leftHand.position.set(
      0,
      garmentGeometry.handOffset.y - 0.048,
      garmentGeometry.handOffset.z + 0.009,
    );
    parts.rightHand.position.copy(parts.leftHand.position);
    parts.leftLeg.position.x = 0.115 * body.hip;
    parts.rightLeg.position.x = -0.115 * body.hip;
    parts.leftTrouser.geometry =
      this.#lowerBodyGeometries.legs[appearance.lowerGarment];
    parts.rightTrouser.geometry = parts.leftTrouser.geometry;
    parts.leftFootwear.geometry =
      this.#lowerBodyGeometries.footwear[appearance.footwear];
    parts.rightFootwear.geometry = parts.leftFootwear.geometry;
    const legWidth = body.limb;
    parts.leftLeg.scale.set(legWidth, 1, legWidth);
    parts.rightLeg.scale.set(legWidth, 1, legWidth);
    parts.skirt.visible = appearance.lowerGarment === "skirt";
    parts.skirt.scale.set(body.hip, 1, body.hip);
    applyGarmentLayerSet(
      parts.garments,
      appearance.outerwear,
      body,
    );
    parts.head.scale.set(
      face.x * age.headScale,
      face.y * age.headScale,
      face.z * age.headScale,
    );
    parts.nose.scale.set(
      appearance.faceShape === "angular" ? 1.08 : 0.92,
      appearance.faceShape === "oval" ? 1.12 : 1,
      1,
    );
    const eyeSpacing = appearance.faceShape === "round" ? 0.075 : 0.069;
    parts.leftEye.position.x = eyeSpacing;
    parts.rightEye.position.x = -eyeSpacing;
    parts.leftEye.scale.setScalar(age.eyeScale);
    parts.rightEye.scale.setScalar(age.eyeScale);
    parts.leftBrow.position.x = eyeSpacing;
    parts.rightBrow.position.x = -eyeSpacing;

    const presentedHairStyle = resolvePresentedHairStyle(appearance);
    parts.hair.visible = presentedHairStyle !== "bald" ||
      appearance.facialHair !== "none";
    parts.hair.geometry = this.#featureGeometries.hair[presentedHairStyle][
      appearance.facialHair
    ];
    parts.hair.position.set(0, 1.72, 0);
    parts.hair.scale.set(
      face.x * age.headScale,
      face.y * age.headScale,
      face.z * age.headScale,
    );

    const hasEyewear = appearance.eyewear !== "none";
    parts.leftLens.visible = hasEyewear;
    parts.rightLens.visible = hasEyewear;
    if (hasEyewear) {
      const lensGeometry = appearance.eyewear === "round_glasses"
        ? this.#featureGeometries.eyewear.round
        : this.#featureGeometries.eyewear.square;
      parts.leftLens.geometry = lensGeometry;
      parts.rightLens.geometry = lensGeometry;
      parts.leftLens.position.x = eyeSpacing;
      parts.rightLens.position.x = -eyeSpacing;
      parts.leftLens.scale.set(1, 1, 1);
      parts.rightLens.scale.set(1, 1, 1);
    }

    parts.headwear.visible = appearance.headwear !== "none";
    if (appearance.headwear !== "none") {
      parts.headwear.geometry = this.#featureGeometries.headwear[
        appearance.headwear
      ];
    }
    parts.headwear.position.set(0, 1.72, 0);
    parts.headwear.scale.set(
      face.x * age.headScale,
      face.y * age.headScale,
      face.z * age.headScale,
    );
    parts.neckwear.visible = appearance.neckwear !== "none";
    if (appearance.neckwear !== "none") {
      parts.neckwear.geometry = this.#featureGeometries.neckwear[
        appearance.neckwear
      ];
    }
    parts.neckwear.scale.set(1, 1, 1);
    parts.carried.visible = appearance.carriedAccessory !== "none";
    if (appearance.carriedAccessory !== "none") {
      parts.carried.geometry = this.#featureGeometries.carried[
        appearance.carriedAccessory
      ];
    }
    const carriedPlacement = APPEARANCE_PRESENTATION_CONFIG
      .carriedPlacementById[appearance.carriedAccessory];
    parts.carried.position.set(
      carriedPlacement.x,
      carriedPlacement.y,
      carriedPlacement.z,
    );
    parts.carried.scale.set(
      carriedPlacement.scaleX,
      carriedPlacement.scaleY,
      carriedPlacement.scaleZ,
    );

    materials.outerwear.color.setHex(appearance.colours.outerwear);
    setGarmentTrimColour(materials.trim, appearance.colours.outerwear);
    materials.lower.color.setHex(appearance.colours.lower);
    materials.boot.color
      .setHex(appearance.colours.lower)
      .offsetHSL(0, -0.02, -0.11);
    materials.accent.color.setHex(appearance.colours.accent);
    materials.skin.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.skinToneById[appearance.skinTone],
    );
    materials.hair.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.hairColourById[appearance.hairColour],
    );
    materials.eyes.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.eyeColourById[appearance.eyeColour],
    );
    materials.accessory.color.setHex(0x343735);
  }

  #applyMotion(
    actor: Actor,
    resident: Readonly<ResidentPresentation>,
    animationTimeSeconds: number,
  ): void {
    const { parts } = actor;
    const phaseOffset = stableMotionPhase(resident.id);
    const isWalking = resident.locomotionSpeedMps > 1e-3;
    const walkingSpeedScale = Math.max(
      0.5,
      Math.min(
        1.5,
        resident.locomotionSpeedMps /
          NEIGHBORHOOD_CONFIG.ambientWalkSpeedMps,
      ),
    );
    const baseCadence = isWalking
      ? 5.2 * walkingSpeedScale
      : resident.activity === "working"
        ? 2.4
        : 0.85;
    const cadence = baseCadence *
      (1 + stableMotionVariation(resident.id)) *
      APPEARANCE_PRESENTATION_CONFIG.agePresentationById[
        resident.appearance.ageGroup
      ].cadenceScale;
    const cycle = animationTimeSeconds * cadence + phaseOffset;
    const wave = Math.sin(cycle);
    const armSplay = CHARACTER_PRESENTATION_CONFIG.armRestSplayRadians;
    const ageStoop = APPEARANCE_PRESENTATION_CONFIG.agePresentationById[
      resident.appearance.ageGroup
    ].postureRadians;
    const baseStoop = Math.max(
      ageStoop,
      resident.elderVariant === "none"
        ? 0
        : CHARACTER_PRESENTATION_CONFIG.elderStoopRadians,
    );

    parts.leftArm.rotation.set(0, 0, armSplay);
    parts.rightArm.rotation.set(0, 0, -armSplay);
    parts.leftArm.position.y = 1.46;
    parts.leftArm.position.z = 0;
    parts.rightArm.position.y = 1.46;
    parts.rightArm.position.z = 0;
    parts.leftLeg.rotation.set(0, 0, 0);
    parts.rightLeg.rotation.set(0, 0, 0);
    parts.leftLeg.position.y = NPC_LEG_PIVOT_HEIGHT_M;
    parts.leftLeg.position.z = 0;
    parts.rightLeg.position.y = NPC_LEG_PIVOT_HEIGHT_M;
    parts.rightLeg.position.z = 0;
    parts.upperBody.rotation.set(baseStoop, 0, 0);
    parts.upperBody.position.set(0, 1.25, 0);
    parts.hips.position.y = 0.83;
    parts.skirt.position.y = 0.43;
    setHeadVerticalOffset(parts, 0);
    parts.head.rotation.z = 0;
    parts.skirt.rotation.z = 0;
    parts.leftBrow.rotation.z = -0.08;
    parts.rightBrow.rotation.z = 0.08;
    parts.mouth.rotation.z = Math.PI;
    parts.mouth.scale.set(1, 0.28, 1);
    const carriedPlacement = APPEARANCE_PRESENTATION_CONFIG
      .carriedPlacementById[resident.appearance.carriedAccessory];
    parts.carried.position.set(
      carriedPlacement.x,
      carriedPlacement.y,
      carriedPlacement.z,
    );
    let motionBobMeters = 0;

    if (isWalking) {
      parts.leftArm.rotation.x = -wave * 0.34;
      parts.rightArm.rotation.x = wave * 0.34;
      parts.leftLeg.rotation.x = wave * 0.44;
      parts.rightLeg.rotation.x = -wave * 0.44;
      parts.upperBody.rotation.z = wave * 0.022;
      parts.upperBody.rotation.x = baseStoop + 0.025;
      parts.upperBody.position.x = wave * 0.012;
      parts.head.rotation.z = -wave * 0.012;
      parts.skirt.rotation.z = wave * 0.012;
      motionBobMeters = Math.abs(Math.sin(cycle)) * 0.018;
    } else if (resident.activity === "working") {
      parts.leftArm.rotation.x = -0.42 + wave * 0.13;
      parts.rightArm.rotation.x = -0.3 - wave * 0.13;
      parts.upperBody.rotation.z = wave * 0.018;
      parts.head.rotation.z = -0.025 + wave * 0.012;
      parts.leftBrow.rotation.z = -0.11 + wave * 0.025;
      parts.rightBrow.rotation.z = 0.11 - wave * 0.025;
      motionBobMeters = (wave + 1) * 0.003;
    } else if (resident.activity === "resting") {
      const restDropMeters = 0.27;
      parts.upperBody.position.y -= restDropMeters;
      parts.hips.position.y -= 0.08;
      parts.skirt.position.y -= 0.08;
      setHeadVerticalOffset(parts, -restDropMeters);
      parts.leftArm.position.y -= 0.24;
      parts.rightArm.position.y -= 0.24;
      parts.leftArm.position.z = 0.08;
      parts.rightArm.position.z = 0.08;
      parts.leftArm.rotation.x = -0.55 + wave * 0.035;
      parts.rightArm.rotation.x = -0.55 - wave * 0.035;
      parts.leftLeg.position.z = 0.18;
      parts.rightLeg.position.z = 0.18;
      parts.leftLeg.rotation.x = -1.15;
      parts.rightLeg.rotation.x = -1.15;
      parts.carried.position.y += carriedPlacement.restingYOffset;
      motionBobMeters = (wave + 1) * 0.002;
    } else {
      const idleSway = wave * 0.012;
      parts.leftArm.rotation.x = idleSway * 0.6;
      parts.rightArm.rotation.x = -idleSway * 0.6;
      parts.upperBody.rotation.z = idleSway;
      parts.upperBody.position.x = wave * 0.014;
      parts.head.rotation.z = -idleSway * 0.55;
      motionBobMeters = (Math.sin(cycle * 2 - Math.PI * 0.5) + 1) * 0.004;
    }
    const expression = resident.expression ?? "neutral";
    if (expression === "warm") {
      parts.mouth.rotation.z = Math.PI;
      parts.mouth.scale.set(1.08, 0.92, 1);
      parts.leftBrow.rotation.z = -0.045;
      parts.rightBrow.rotation.z = 0.045;
    } else if (expression === "wry") {
      parts.mouth.rotation.z = Math.PI + 0.18;
      parts.mouth.scale.set(0.92, 0.62, 1);
      parts.leftBrow.rotation.z = -0.15;
      parts.rightBrow.rotation.z = 0.035;
    } else if (expression === "concerned") {
      parts.mouth.rotation.z = 0;
      parts.mouth.scale.set(0.94, 0.72, 1);
      parts.leftBrow.rotation.z = 0.09;
      parts.rightBrow.rotation.z = -0.09;
    }
    parts.presentationRoot.position.y = npcGroundOffsetM(
      actor,
      resident.appearance,
      parts.leftLeg.rotation.x,
      parts.rightLeg.rotation.x,
    ) + motionBobMeters;
  }

  #createActor(index: number): Actor {
    const object = new Group();
    object.name = "resident-slot:" + index;
    object.visible = false;
    const presentationRoot = new Group();
    presentationRoot.name = "resident-presentation:" + index;
    presentationRoot.position.y =
      CHARACTER_PRESENTATION_CONFIG.soleClearanceMeters;

    const materials: ActorMaterials = {
      outerwear: createMaterial("resident-outerwear:" + index, 0x596259),
      lower: createMaterial("resident-lower:" + index, 0x343b3b),
      accent: createMaterial("resident-accent:" + index, 0xa6795b, 0.9),
      skin: createMaterial("resident-skin:" + index, 0xb89a7b, 0.92),
      hair: createMaterial("resident-hair:" + index, 0x5d4a3f, 0.96),
      eyes: createMaterial("resident-eyes:" + index, 0x6f7b7c, 0.82),
      accessory: createMaterial("resident-accessory:" + index, 0x343735, 0.8),
      boot: createMaterial("resident-boot:" + index, 0x242826, 0.88),
      trim: createMaterial("resident-garment-trim:" + index, 0x3f4641, 0.97),
    };
    materials.trim.side = DoubleSide;
    const hips = setShadow(new Mesh(this.#shared.hips, materials.lower));
    hips.name = "hips";
    hips.position.y = 0.83;
    const upperBody = new Group();
    upperBody.name = "upper-body";
    upperBody.position.y = 1.25;
    const garments = createGarmentLayerSet(
      this.#garmentGeometries,
      materials.outerwear,
      materials.trim,
      materials.accent,
      "resident-garments:" + index,
    );
    upperBody.add(garments.object);
    const belly = setShadow(new Mesh(this.#shared.belly, materials.outerwear));
    belly.name = "belly-shape";
    belly.position.set(0, -0.06, 0.22);
    belly.visible = false;
    upperBody.add(belly);
    const leftArm = new Group();
    leftArm.name = "left-arm";
    leftArm.position.set(
      CHARACTER_PRESENTATION_CONFIG.proportions.shoulderJointOffsetMeters,
      1.46,
      0,
    );
    const leftSleeve = setShadow(new Mesh(
      this.#garmentGeometries.wool_coat.sleeve,
      materials.outerwear,
    ));
    leftSleeve.name = "left-sleeve";
    leftArm.add(leftSleeve);
    const leftHand = setShadow(new Mesh(this.#shared.hand, materials.skin));
    leftHand.name = "left-hand";
    leftHand.scale.set(0.86, 1, 0.72);
    leftArm.add(leftHand);
    const rightArm = new Group();
    rightArm.name = "right-arm";
    rightArm.position.set(
      -CHARACTER_PRESENTATION_CONFIG.proportions.shoulderJointOffsetMeters,
      1.46,
      0,
    );
    const rightSleeve = setShadow(new Mesh(
      this.#garmentGeometries.wool_coat.sleeve,
      materials.outerwear,
    ));
    rightSleeve.name = "right-sleeve";
    rightArm.add(rightSleeve);
    const rightHand = setShadow(new Mesh(this.#shared.hand, materials.skin));
    rightHand.name = "right-hand";
    rightHand.scale.set(0.86, 1, 0.72);
    rightArm.add(rightHand);
    const leftLeg = new Group();
    leftLeg.name = "left-leg";
    leftLeg.position.set(0.115, NPC_LEG_PIVOT_HEIGHT_M, 0);
    const leftTrouser = setShadow(new Mesh(
      this.#lowerBodyGeometries.legs.trousers,
      materials.lower,
    ));
    leftTrouser.name = "left-trouser";
    leftLeg.add(leftTrouser);
    const leftBoot = setShadow(new Mesh(
      this.#lowerBodyGeometries.footwear.walking_boots,
      materials.boot,
    ));
    leftBoot.name = "left-boot";
    leftBoot.position.set(
      0,
      NPC_FOOTWEAR_LOCAL_Y_M,
      NPC_FOOTWEAR_LOCAL_Z_M,
    );
    leftLeg.add(leftBoot);
    const rightLeg = new Group();
    rightLeg.name = "right-leg";
    rightLeg.position.set(-0.115, NPC_LEG_PIVOT_HEIGHT_M, 0);
    const rightTrouser = setShadow(new Mesh(
      this.#lowerBodyGeometries.legs.trousers,
      materials.lower,
    ));
    rightTrouser.name = "right-trouser";
    rightLeg.add(rightTrouser);
    const rightBoot = setShadow(
      new Mesh(this.#lowerBodyGeometries.footwear.walking_boots, materials.boot),
    );
    rightBoot.name = "right-boot";
    rightBoot.position.set(
      0,
      NPC_FOOTWEAR_LOCAL_Y_M,
      NPC_FOOTWEAR_LOCAL_Z_M,
    );
    rightLeg.add(rightBoot);
    const skirt = setShadow(new Mesh(this.#shared.skirt, materials.lower));
    skirt.name = "skirt";
    skirt.position.y = 0.43;
    skirt.visible = false;
    const head = setShadow(new Mesh(this.#shared.head, materials.skin));
    head.name = "face";
    head.position.y = 1.72;
    const nose = setShadow(new Mesh(this.#shared.nose, materials.skin));
    nose.name = "nose";
    nose.position.set(0, 1.715, NPC_HEAD_RADIUS_M * 0.99);
    nose.rotation.x = Math.PI * 0.5;
    const mouth = new Mesh(this.#shared.mouth, materials.accessory);
    mouth.name = "mouth";
    mouth.position.set(0, 1.635, NPC_HEAD_RADIUS_M * 0.96);
    mouth.rotation.z = Math.PI;
    const leftEye = new Mesh(this.#shared.eye, materials.eyes);
    leftEye.name = "left-eye";
    leftEye.position.set(0.069, 1.765, NPC_HEAD_RADIUS_M * 0.91);
    const rightEye = new Mesh(this.#shared.eye, materials.eyes);
    rightEye.name = "right-eye";
    rightEye.position.set(-0.069, 1.765, NPC_HEAD_RADIUS_M * 0.91);
    const leftBrow = new Mesh(this.#shared.brow, materials.hair);
    leftBrow.name = "left-brow";
    leftBrow.position.set(0.069, 1.825, NPC_HEAD_RADIUS_M * 0.94);
    leftBrow.rotation.z = -0.08;
    const rightBrow = new Mesh(this.#shared.brow, materials.hair);
    rightBrow.name = "right-brow";
    rightBrow.position.set(-0.069, 1.825, NPC_HEAD_RADIUS_M * 0.94);
    rightBrow.rotation.z = 0.08;
    const hair = setShadow(new Mesh(
      this.#featureGeometries.hair.parted.none,
      materials.hair,
    ));
    hair.name = "hair";
    hair.position.y = 1.72;
    const leftLens = new Mesh(
      this.#featureGeometries.eyewear.round,
      materials.accessory,
    );
    leftLens.name = "left-lens";
    leftLens.position.set(0.069, 1.765, NPC_HEAD_RADIUS_M * 1.04);
    const rightLens = new Mesh(
      this.#featureGeometries.eyewear.round,
      materials.accessory,
    );
    rightLens.name = "right-lens";
    rightLens.position.set(-0.069, 1.765, NPC_HEAD_RADIUS_M * 1.04);
    const headwear = setShadow(
      new Mesh(this.#featureGeometries.headwear.knit_cap, materials.accent),
    );
    headwear.name = "headwear";
    headwear.position.y = 1.72;
    const neckwear = setShadow(
      new Mesh(this.#featureGeometries.neckwear.scarf, materials.accent),
    );
    neckwear.name = "neckwear";
    neckwear.position.set(0, 1.52, 0);
    const carried = setShadow(
      new Mesh(this.#featureGeometries.carried.satchel, materials.accent),
    );
    carried.name = "carried-accessory";
    carried.position.set(0.36, 0.89, -0.03);

    presentationRoot.add(
      hips,
      upperBody,
      leftArm,
      rightArm,
      leftLeg,
      rightLeg,
      skirt,
      head,
      nose,
      mouth,
      leftBrow,
      rightBrow,
      leftEye,
      rightEye,
      hair,
      leftLens,
      rightLens,
      headwear,
      neckwear,
      carried,
    );
    object.add(presentationRoot);
    this.object.add(object);
    return {
      object,
      materials,
      parts: {
        presentationRoot,
        upperBody,
        hips,
        garments,
        belly,
        leftArm,
        rightArm,
        leftSleeve,
        rightSleeve,
        leftHand,
        rightHand,
        leftLeg,
        rightLeg,
        leftTrouser,
        rightTrouser,
        leftFootwear: leftBoot,
        rightFootwear: rightBoot,
        skirt,
        head,
        nose,
        mouth,
        leftBrow,
        rightBrow,
        leftEye,
        rightEye,
        hair,
        leftLens,
        rightLens,
        headwear,
        neckwear,
        carried,
      },
      slotIndex: index,
      residentId: null,
      appearance: null,
    };
  }

}
